/**
 * The words the primitives themselves say — "copy", "next page", "saved".
 *
 * Everything a *page* says comes from `@/lib/i18n` and reaches a primitive as
 * a prop. These are the few that belong to the control rather than the page:
 * a CopyButton says "copied" wherever it sits.
 *
 * All of it lives in the catalogue: shared words under their shared keys
 * (`common.*`, `nav.*`, `password.*`) and the primitives' own under `ui.*` in
 * `lib/i18n/ui.ts`. `ui(key)` is the short name the components use; a
 * `UiKey` that has no catalogue entry does not compile.
 */
import { t, type MessageKey } from '@/lib/i18n';

const FROM_CATALOGUE = {
  close: 'common.close',
  retry: 'common.retry',
  loading: 'common.loading',
  cancel: 'common.cancel',
  actions: 'common.actions',
  loadMore: 'common.loadMore',
  listSeparator: 'common.listSeparator',
  showPassword: 'password.show',
  hidePassword: 'password.hide',
  toLight: 'nav.toLight',
  toDark: 'nav.toDark',
  account: 'nav.account',
  signOut: 'nav.signOut',
} as const satisfies Record<string, MessageKey>;

type OwnKey = { [K in MessageKey]: K extends `ui.${infer Name}` ? Name : never }[MessageKey];

export type UiKey = keyof typeof FROM_CATALOGUE | OwnKey;

export function ui(key: UiKey, values?: Record<string, string | number>): string {
  const catalogueKey: MessageKey = key in FROM_CATALOGUE ? FROM_CATALOGUE[key as keyof typeof FROM_CATALOGUE] : (`ui.${key}` as MessageKey);

  return t(catalogueKey, values);
}

/** Joins class names, dropping the falsy ones. */
export function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}
